/**
 * Submit Title Review Module
 * Final step - collects all data from localStorage and submits to server
 */
(function () {
  "use strict";

  // Run once per page (see submission-session-manager.js).
  if (window.submitTitleReviewLoaded) {
    return;
  }
  window.submitTitleReviewLoaded = true;

  function getApplyBasePath() {
    return window.submissionApplyBasePath || "/partners/bloodstream/apply";
  }

  function getThemeKey() {
    return window.submissionThemeKey || "bloodstream";
  }

  /**
   * Escape a value for HTML text or a quoted attribute. Every value from the
   * session (typed by the partner, restored from the server by a resume link,
   * or returned by an API) goes through this before it reaches innerHTML.
   */
  function esc(value) {
    return String(value === null || value === undefined ? "" : value)
      .replace(/&/g, "&amp;")
      .replace(/</g, "&lt;")
      .replace(/>/g, "&gt;")
      .replace(/"/g, "&quot;")
      .replace(/'/g, "&#39;");
  }

  /** Escaped URL for href/src: only http(s) and same-site paths, else "#". */
  function safeUrl(value) {
    const url = String(value === null || value === undefined ? "" : value).trim();
    // Browsers read "/\host" and "/<tab>/host" as protocol-relative.
    if (/[\\\s]/.test(url)) {
      return "#";
    }
    if (/^https?:\/\//i.test(url) || /^\/(?![\/\\])/.test(url)) {
      return esc(url);
    }
    return "#";
  }

  const sessionManager = window.SubmissionSessionManager;
  let capSolver = null;

  /**
   * Initialize the module
   */
  function init() {
    if (!document.getElementById("submit-title-review")) {
      return;
    }

    if (!sessionManager) {
      console.error("Session manager not loaded");
      return;
    }

    // Load existing session or redirect if none
    if (!sessionManager.hasSession()) {
      console.error("No session found, redirecting to start");
      window.location.href = `${getApplyBasePath()}/imdb`;
      return;
    }

    // Check if session was already submitted and redirect if so
    if (sessionManager.checkAndRedirectIfSubmitted()) {
      return; // Exit init - redirect is happening
    }

    // Set current step
    sessionManager.setCurrentStep(5);

    // Load title information card in sidebar
    loadTitleInfoCard();

    // Load and display all data from previous steps
    loadReviewData();

    // Setup form handlers
    setupEditButtons();
    setupContactForm();
    setupTermsCheckboxes();
    setupSubmitButton();
    setupNavigation();

    // Initialize CAP if enabled
    initializeCAP();

    // Update progress
    updateProgress();
  }

  /**
   * Load and display title information in sidebar card
   */
  function loadTitleInfoCard() {
    const step1Data = sessionManager.getStepData(1);
    const step3Data = sessionManager.getStepData(3);

    if (!step1Data.title_name) {
      return;
    }

    const titleName = document.getElementById("title-name");
    const titleYear = document.getElementById("title-year");
    const titleGenres = document.getElementById("title-genres");
    const titlePoster = document.getElementById("title-poster");
    const titlePosterImg = document.getElementById("title-poster-img");

    // Update title name (decode HTML entities)
    if (titleName) {
      const decodedTitle = window.HtmlEntityDecoder
        ? window.HtmlEntityDecoder.decode(
            step1Data.title_name || "Unknown Title"
          )
        : step1Data.title_name || "Unknown Title";
      titleName.textContent = decodedTitle;
    }

    // Update year
    if (titleYear && step1Data.year) {
      titleYear.textContent = `Year: ${step1Data.year}`;
    }

    // Update genres
    if (titleGenres && step1Data.genres) {
      titleGenres.textContent = `Genres: ${step1Data.genres}`;
    }

    // Use customization poster if available, otherwise fall back to IMDB poster
    // Priority: custom uploaded poster > selected TMDB/IMDB poster > default IMDB poster
    let posterUrl = step1Data.poster_url; // Default fallback

    if (step3Data.custom_poster_url && step3Data.custom_poster_data) {
      // Use uploaded custom poster (base64 encoded)
      posterUrl = step3Data.custom_poster_data.url;
    } else if (step3Data.selected_poster) {
      // Use selected TMDB/IMDB poster
      posterUrl = step3Data.selected_poster;
    }

    // Show and load poster image if available
    if (titlePosterImg && posterUrl) {
      titlePosterImg.src = posterUrl;
      const decodedTitleForAlt = window.HtmlEntityDecoder
        ? window.HtmlEntityDecoder.decode(step1Data.title_name)
        : step1Data.title_name;
      titlePosterImg.alt = `${decodedTitleForAlt} Poster`;

      if (titlePoster) {
        titlePoster.classList.remove("hide");
      }

      // Handle image load/error
      titlePosterImg.onload = () => {
        console.log("Poster loaded successfully");
      };

      titlePosterImg.onerror = () => {
        console.log("Failed to load poster image");
        if (titlePoster) {
          titlePoster.classList.add("hide");
        }
      };
    }

    console.log("Title info card displayed:", {
      movieTitle: step1Data.title_name,
      year: step1Data.year,
      genres: step1Data.genres,
      posterUrl: posterUrl,
      customPosterUrl: step3Data.custom_poster_url,
      selectedPoster: step3Data.selected_poster,
    });
  }

  /**
   * Load and display all review data
   */
  async function loadReviewData() {
    // First, refresh any expired image URLs
    await refreshExpiredImageUrls();

    // Load step 1 data - Title Information
    loadTitleInformation();

    // Load step 2 data - Upload Content
    loadUploadContent();

    // Load step 3 data - Customizations
    loadCustomizations();

    // Load step 4 data - Distribution & Rights
    loadDistribution();

    // Load contact information if saved
    loadContactInformation();
  }

  /**
   * Refresh expired image URLs from step 3
   */
  async function refreshExpiredImageUrls() {
    const step3Data = sessionManager.getStepData(3);

    // Check if we have any custom image data that might have expired URLs
    const hasCustomImages =
      step3Data.custom_poster_data ||
      step3Data.custom_backdrop_data ||
      step3Data.custom_still_data ||
      step3Data.custom_treatment_data ||
      step3Data.custom_logo_data;

    if (!hasCustomImages) {
      return;
    }

    console.log("[Review] Refreshing image URLs for review page...");

    try {
      const response = await fetch("/api/submissions/refresh-urls", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          uuid: sessionManager.getSessionId(),
          step: 3,
        }),
      });

      const result = await response.json();

      if (!result.success) {
        console.error("[Review] Failed to refresh URLs:", result.message);
        return;
      }

      const refreshedUrls = result.data?.refreshed_urls || {};
      console.log("[Review] Received refreshed URLs:", refreshedUrls);

      // Update step3Data with new URLs
      let updated = false;
      Object.keys(refreshedUrls).forEach((key) => {
        const match = key.match(/^(\w+)_url$/);
        if (match) {
          const type = match[1];
          const dataKey = `custom_${type}_data`;

          if (step3Data[dataKey]) {
            // Update the URL in the data object
            step3Data[dataKey].url = refreshedUrls[key];
            // Update expires_at if provided
            if (refreshedUrls[`${type}_expires_at`]) {
              step3Data[dataKey].expires_at =
                refreshedUrls[`${type}_expires_at`];
            }
            console.log(`[Review] Updated ${type} URL:`, refreshedUrls[key]);
            updated = true;
          }
        }
      });

      // Save updated data back to session if anything was updated
      if (updated) {
        sessionManager.saveStepData(3, step3Data);
        console.log("[Review] URLs refreshed and saved to session");
      }
    } catch (error) {
      console.error("[Review] Error refreshing URLs:", error);
    }
  }

  /**
   * Load title information from step 1
   */
  function loadTitleInformation() {
    const container = document.getElementById("title-info-review");
    if (!container) return;

    const step1Data = sessionManager.getStepData(1);

    if (!step1Data.imdb_id) {
      container.innerHTML =
        '<p class="text-danger">No title information found. Please start from the beginning.</p>';
      return;
    }

    // Decode HTML entities for display
    const decodedTitle = window.HtmlEntityDecoder
      ? window.HtmlEntityDecoder.decode(step1Data.title_name || "N/A")
      : step1Data.title_name || "N/A";

    const contentTypeLabels = {
      feature: "Feature Film",
      episodic: "Episodic / Series",
      standalone_trailer: "Standalone Trailer",
      other: "Other",
    };
    const contentTypeLabel = contentTypeLabels[step1Data.content_type] || step1Data.content_type || "Feature Film";

    const html = `
      <div class="grid-x grid-margin-x-large">
        <div class="cell medium-6">
          <dl class="info-list mb-3">
            <dt class="mb-0">Title</dt>
            <dd>${esc(decodedTitle)}</dd>

            <dt class="mb-0">IMDB ID</dt>
            <dd>${esc(step1Data.imdb_id || "N/A")}</dd>

            <dt class="mb-0">Content Type</dt>
            <dd>${esc(contentTypeLabel)}</dd>
          </dl>
        </div>
        <div class="cell medium-6">
          <dl class="info-list mb-3">
            <dt class="mb-0">Year</dt>
            <dd>${esc(step1Data.year || "N/A")}</dd>

            <dt class="mb-0">Genres</dt>
            <dd>${esc(step1Data.genres || "N/A")}</dd>
            ${step1Data.director ? `
            <dt class="mb-0">Director</dt>
            <dd>${esc(step1Data.director)}</dd>
            ` : ""}
          </dl>
        </div>
      </div>
    `;

    container.innerHTML = html;
  }

  /**
   * Load upload content from step 2
   */
  function loadUploadContent() {
    const container = document.getElementById("upload-content-review");
    if (!container) return;

    const step2Data = sessionManager.getStepData(2);

    let html = '<div class="grid-x grid-margin-x-large">';

    // Trailer/Promo section (left column)
    html += '<div class="cell medium-6">';
    html += '<h4 class="mb-3">Trailer / Promo Kit</h4>';

    // Check for uploaded files (trailer_uploads) or link
    const hasTrailerUploads = step2Data.trailer_uploads?.length > 0;
    const hasTrailerLink = step2Data.trailer_link?.trim().length > 0;

    if (hasTrailerUploads) {
      html += '<dl class="info-list">';
      html += "<dt>Files</dt>";
      html += "<dd>";
      step2Data.trailer_uploads.forEach((fileData) => {
        // Extract filename from file upload event structure
        // fileData = { file: File, result: {...} }
        const fileName =
          fileData.file?.name ||
          fileData.name ||
          fileData.filename ||
          fileData.file_name ||
          fileData.originalName ||
          fileData.result?.file_name ||
          "Uploaded file";
        const fileSize = fileData.file?.size || fileData.size;
        const fileSizeStr = fileSize ? ` (${formatFileSize(fileSize)})` : "";
        html += `<div class="mb-1"><i class="fas fa-file text-muted"></i> ${esc(fileName)}${esc(fileSizeStr)}</div>`;
      });
      html += "</dd>";
      html += "</dl>";
    } else if (hasTrailerLink) {
      html += '<dl class="info-list">';
      html += "<dt>Link</dt>";
      html += `<dd><a href="${safeUrl(step2Data.trailer_link)}" target="_blank" rel="noopener noreferrer">${esc(step2Data.trailer_link)} <i class="fas fa-external-link-alt text-xs"></i></a></dd>`;
      if (step2Data.trailer_password) {
        html += "<dt>Password</dt>";
        html += `<dd><code>${esc(step2Data.trailer_password)}</code></dd>`;
      }
      html += "</dl>";
    } else {
      html += '<p class="text-muted">No trailer provided</p>';
    }
    html += "</div>";

    // Feature/Full Content section (right column)
    html += '<div class="cell medium-6">';
    html += '<h4 class="mb-3">Full Content</h4>';

    // Check for uploaded files (feature_uploads) or link
    const hasFeatureUploads = step2Data.feature_uploads?.length > 0;
    const hasFeatureLink = step2Data.feature_link?.trim().length > 0;

    if (hasFeatureUploads) {
      html += '<dl class="info-list">';
      html += "<dt>Files</dt>";
      html += "<dd>";
      step2Data.feature_uploads.forEach((fileData) => {
        // Extract filename from file upload event structure
        // fileData = { file: File, result: {...} }
        const fileName =
          fileData.file?.name ||
          fileData.name ||
          fileData.filename ||
          fileData.file_name ||
          fileData.originalName ||
          fileData.result?.file_name ||
          "Uploaded file";
        const fileSize = fileData.file?.size || fileData.size;
        const fileSizeStr = fileSize ? ` (${formatFileSize(fileSize)})` : "";
        html += `<div class="mb-1"><i class="fas fa-file text-muted"></i> ${esc(fileName)}${esc(fileSizeStr)}</div>`;
      });
      html += "</dd>";
      html += "</dl>";
    } else if (hasFeatureLink) {
      html += '<dl class="info-list">';
      html += "<dt>Link</dt>";
      html += `<dd><a href="${safeUrl(step2Data.feature_link)}" target="_blank" rel="noopener noreferrer">${esc(step2Data.feature_link)} <i class="fas fa-external-link-alt text-xs"></i></a></dd>`;
      if (step2Data.feature_password) {
        html += "<dt>Password</dt>";
        html += `<dd><code>${esc(step2Data.feature_password)}</code></dd>`;
      }
      html += "</dl>";
    } else {
      html += '<p class="text-muted">No full content provided</p>';
    }
    html += "</div>";

    html += "</div>"; // Close grid-x

    // Subtitles section (full width below)
    const hasSubtitleUploads = step2Data.subtitle_uploads?.length > 0;
    if (hasSubtitleUploads) {
      html += '<div class="mt-4">';
      html += '<h4 class="mb-3">Subtitles</h4>';
      html += '<dl class="info-list">';
      html += "<dt>Files</dt>";
      html += "<dd>";
      step2Data.subtitle_uploads.forEach((fileData) => {
        // Extract filename from file upload event structure
        // fileData = { file: File, result: {...} }
        const fileName =
          fileData.file?.name ||
          fileData.name ||
          fileData.filename ||
          fileData.file_name ||
          fileData.originalName ||
          fileData.result?.file_name ||
          "Subtitle file";
        const fileSize = fileData.file?.size || fileData.size;
        const fileSizeStr = fileSize ? ` (${formatFileSize(fileSize)})` : "";
        html += `<div class="mb-1"><i class="fas fa-closed-captioning text-muted"></i> ${esc(fileName)}${esc(fileSizeStr)}</div>`;
      });
      html += "</dd>";
      html += "</dl>";
      html += "</div>";
    }

    // Upload notes (full width below)
    if (step2Data.upload_notes) {
      html += '<div class="mt-4">';
      html += '<h4 class="mb-2">Additional Notes</h4>';
      html += `<p class="text-muted">${esc(step2Data.upload_notes)}</p>`;
      html += "</div>";
    }

    container.innerHTML = html;
  }

  /**
   * Load customizations from step 3
   */
  function loadCustomizations() {
    const container = document.getElementById("customizations-review");
    if (!container) return;

    const step3Data = sessionManager.getStepData(3);
    console.log("[Review] Loading customizations from step 3:", step3Data);

    let html = "";

    // Check if step was skipped
    if (step3Data.skipped) {
      html = '<p class="text-muted">No customizations - step was skipped</p>';
      container.innerHTML = html;
      return;
    }

    // Selected images section - check for custom uploads first, then selected images
    const hasPoster = step3Data.custom_poster_url || step3Data.selected_poster;
    const hasBackdrop =
      step3Data.custom_backdrop_url || step3Data.selected_backdrop;
    const hasStill = step3Data.custom_still_url || step3Data.selected_still;
    const hasTreatment = step3Data.custom_treatment_url;
    const hasLogo = step3Data.custom_logo_url || step3Data.selected_logo;

    if (hasPoster || hasBackdrop || hasStill || hasTreatment || hasLogo) {
      html += '<div class="form-group mb-4">';
      // Center the grid horizontally if there's no treatment
      const centerStyle = !hasTreatment
        ? "align-items: flex-start; justify-content: center;"
        : "align-items: flex-start;";
      html += `<div class="grid-x grid-margin-x" style="${centerStyle}">`;

      if (hasPoster) {
        // Determine poster URL - prioritize custom upload data
        let posterUrl = null;
        let isCustomUpload = false;

        if (
          step3Data.custom_poster_url &&
          step3Data.custom_poster_url.startsWith("upload:")
        ) {
          // Custom upload - get URL from custom_poster_data
          if (
            step3Data.custom_poster_data &&
            step3Data.custom_poster_data.url
          ) {
            posterUrl = step3Data.custom_poster_data.url;
            isCustomUpload = true;
            console.log("[Review] Using custom poster from data object");
          } else {
            console.error(
              "[Review] Custom poster URL identifier exists but no data found!",
              step3Data.custom_poster_url
            );
          }
        } else if (step3Data.selected_poster) {
          // TMDB/IMDB selected poster
          posterUrl = step3Data.selected_poster;
          console.log("[Review] Using selected poster from TMDB/IMDB");
        }

        console.log(
          "[Review] Poster - custom_poster_url:",
          step3Data.custom_poster_url
        );
        console.log(
          "[Review] Poster - custom_poster_data:",
          step3Data.custom_poster_data
        );
        console.log(
          "[Review] Final Poster URL:",
          posterUrl ? posterUrl.substring(0, 100) : "none"
        );

        if (posterUrl) {
          html += `
            <div class="cell medium-2 text-center mb-3">
              <label class="text-muted small mb-2 d-block">Poster</label>
              <a href="${safeUrl(posterUrl)}" target="_blank" rel="noopener noreferrer" title="Click to open full size in new tab" style="display: block;">
                <img src="${safeUrl(posterUrl)}" alt="Selected Poster" style="max-height: 250px; width: auto; border-radius: 4px; cursor: pointer; border: 1px solid rgba(255,255,255,0.1); display: block; margin: 0 auto;">
              </a>
              <div class="text-xs text-muted mt-2">${
                isCustomUpload ? "Custom Upload" : "From Database"
              }</div>
            </div>
          `;
        }
      }

      if (hasBackdrop) {
        // Determine backdrop URL - prioritize custom upload data
        let backdropUrl = null;
        let isCustomUpload = false;

        if (
          step3Data.custom_backdrop_url &&
          step3Data.custom_backdrop_url.startsWith("upload:")
        ) {
          // Custom upload - get URL from custom_backdrop_data
          if (
            step3Data.custom_backdrop_data &&
            step3Data.custom_backdrop_data.url
          ) {
            backdropUrl = step3Data.custom_backdrop_data.url;
            isCustomUpload = true;
            console.log("[Review] Using custom backdrop from data object");
          } else {
            console.error(
              "[Review] Custom backdrop URL identifier exists but no data found!",
              step3Data.custom_backdrop_url
            );
          }
        } else if (step3Data.selected_backdrop) {
          // TMDB/IMDB selected backdrop
          backdropUrl = step3Data.selected_backdrop;
          console.log("[Review] Using selected backdrop from TMDB/IMDB");
        }

        console.log(
          "[Review] Backdrop - custom_backdrop_url:",
          step3Data.custom_backdrop_url
        );
        console.log(
          "[Review] Backdrop - custom_backdrop_data:",
          step3Data.custom_backdrop_data
        );
        console.log(
          "[Review] Final Backdrop URL:",
          backdropUrl ? backdropUrl.substring(0, 100) : "none"
        );

        if (backdropUrl) {
          html += `
            <div class="cell medium-5 text-center mb-3">
              <label class="text-muted small mb-2 d-block">Backdrop</label>
              <a href="${safeUrl(backdropUrl)}" target="_blank" rel="noopener noreferrer" title="Click to open full size in new tab" style="display: block;">
                <img src="${safeUrl(backdropUrl)}" alt="Selected Backdrop" style="max-height: 250px; width: auto; border-radius: 4px; cursor: pointer; border: 1px solid rgba(255,255,255,0.1); display: block; margin: 0 auto;">
              </a>
              <div class="text-xs text-muted mt-2">${
                isCustomUpload ? "Custom Upload" : "From Database"
              }</div>
            </div>
          `;
        }
      }

      // Backdrop/Hero (Still) - required image
      if (hasStill) {
        // Determine still URL - prioritize custom upload data
        let stillUrl = null;
        let isCustomUpload = false;

        if (
          step3Data.custom_still_url &&
          step3Data.custom_still_url.startsWith("upload:")
        ) {
          // Custom upload - get URL from custom_still_data
          if (step3Data.custom_still_data && step3Data.custom_still_data.url) {
            stillUrl = step3Data.custom_still_data.url;
            isCustomUpload = true;
            console.log("[Review] Using custom still from data object");
          } else {
            console.error(
              "[Review] Custom still URL identifier exists but no data found!",
              step3Data.custom_still_url
            );
          }
        } else if (step3Data.selected_still) {
          // TMDB/IMDB selected still
          stillUrl = step3Data.selected_still;
          console.log("[Review] Using selected still from TMDB/IMDB");
        }

        console.log(
          "[Review] Still - custom_still_url:",
          step3Data.custom_still_url
        );
        console.log(
          "[Review] Still - custom_still_data:",
          step3Data.custom_still_data
        );
        console.log(
          "[Review] Final Still URL:",
          stillUrl ? stillUrl.substring(0, 100) : "none"
        );

        if (stillUrl) {
          html += `
            <div class="cell medium-5 text-center mb-3">
              <label class="text-muted small mb-2 d-block">Backdrop/Hero</label>
              <a href="${safeUrl(stillUrl)}" target="_blank" rel="noopener noreferrer" title="Click to open full size in new tab" style="display: block;">
                <img src="${safeUrl(stillUrl)}" alt="Selected Backdrop/Hero" style="max-height: 250px; width: auto; border-radius: 4px; cursor: pointer; border: 1px solid rgba(255,255,255,0.1); display: block; margin: 0 auto;">
              </a>
              <div class="text-xs text-muted mt-2">${
                isCustomUpload ? "Custom Upload" : "From Database"
              }</div>
            </div>
          `;
        }
      }

      // Title Treatment - custom upload only (dimensions from theme config)
      if (hasTreatment) {
        // Determine treatment URL from custom upload data
        let treatmentUrl = null;

        if (
          step3Data.custom_treatment_url &&
          step3Data.custom_treatment_url.startsWith("upload:")
        ) {
          // Custom upload - get URL from custom_treatment_data
          if (
            step3Data.custom_treatment_data &&
            step3Data.custom_treatment_data.url
          ) {
            treatmentUrl = step3Data.custom_treatment_data.url;
            console.log("[Review] Using custom treatment from data object");
          } else {
            console.error(
              "[Review] Custom treatment URL identifier exists but no data found!",
              step3Data.custom_treatment_url
            );
          }
        }

        console.log(
          "[Review] Treatment - custom_treatment_url:",
          step3Data.custom_treatment_url
        );
        console.log(
          "[Review] Treatment - custom_treatment_data:",
          step3Data.custom_treatment_data
        );
        console.log(
          "[Review] Final Treatment URL:",
          treatmentUrl ? treatmentUrl.substring(0, 100) : "none"
        );

        const treatmentSpec =
          window.themeConfig &&
          window.themeConfig.assetSpecs &&
          window.themeConfig.assetSpecs.treatment;
        const treatmentLabel = treatmentSpec
          ? treatmentSpec.label_short
          : "550 × 123px";
        const treatmentMaxHeight = treatmentSpec
          ? Math.min(treatmentSpec.height, 250)
          : 123;

        if (treatmentUrl) {
          html += `
            <div class="cell medium-5 text-center mb-3">
              <label class="text-muted small mb-2 d-block">Title Treatment</label>
              <a href="${safeUrl(treatmentUrl)}" target="_blank" rel="noopener noreferrer" title="Click to open full size in new tab" style="display: block;">
                <img src="${safeUrl(treatmentUrl)}" alt="Title Treatment" style="max-height: ${treatmentMaxHeight}px; width: auto; border-radius: 4px; cursor: pointer; border: 1px solid rgba(255,255,255,0.1); display: block; margin: 0 auto;">
              </a>
              <div class="text-xs text-muted mt-2">Custom Upload (${esc(treatmentLabel)})</div>
            </div>
          `;
        }
      }

      // Logo - always show the column
      if (hasLogo) {
        // Determine logo URL - prioritize custom upload data
        let logoUrl = null;
        let isCustomUpload = false;

        if (
          step3Data.custom_logo_url &&
          step3Data.custom_logo_url.startsWith("upload:")
        ) {
          // Custom upload - get URL from custom_logo_data
          if (step3Data.custom_logo_data && step3Data.custom_logo_data.url) {
            logoUrl = step3Data.custom_logo_data.url;
            isCustomUpload = true;
            console.log("[Review] Using custom logo from data object");
          } else {
            console.error(
              "[Review] Custom logo URL identifier exists but no data found!",
              step3Data.custom_logo_url
            );
          }
        } else if (step3Data.selected_logo) {
          // TMDB/IMDB selected logo
          logoUrl = step3Data.selected_logo;
          console.log("[Review] Using selected logo from TMDB/IMDB");
        }

        console.log(
          "[Review] Logo - custom_logo_url:",
          step3Data.custom_logo_url
        );
        console.log(
          "[Review] Logo - custom_logo_data:",
          step3Data.custom_logo_data
        );
        console.log(
          "[Review] Final Logo URL:",
          logoUrl ? logoUrl.substring(0, 100) : "none"
        );

        if (logoUrl) {
          html += `
            <div class="cell medium-5 text-center mb-3">
              <label class="text-muted small mb-2 d-block">Logo</label>
              <a href="${safeUrl(logoUrl)}" target="_blank" rel="noopener noreferrer" title="Click to open full size in new tab" style="display: block;">
                <img src="${safeUrl(logoUrl)}" alt="Selected Logo" style="max-height: 200px; width: auto; border-radius: 4px; cursor: pointer; border: 1px solid rgba(255,255,255,0.1); display: block; margin: 0 auto;">
              </a>
              <div class="text-xs text-muted mt-2">${
                isCustomUpload ? "Custom Upload (PNG)" : "From Database"
              }</div>
            </div>
          `;
        }
      }

      html += "</div>";
      html += "</div>";
    }

    if (!html) {
      html = '<p class="text-muted">No customizations selected</p>';
    }

    container.innerHTML = html;
  }

  /**
   * Load distribution & rights from step 4
   */
  function loadDistribution() {
    const container = document.getElementById("distribution-review");
    if (!container) return;

    const step4Data = sessionManager.getStepData(4);
    console.log("[Review] Loading distribution from step 4:", step4Data);

    let html = '<div class="grid-x grid-margin-x-large">';

    // Territory information
    html += '<div class="cell medium-6">';
    html += '<h4 class="mb-3">Distribution Territory</h4>';
    html += '<dl class="info-list">';

    const territoryLabels = {
      worldwide: "Worldwide",
      english_speaking: "All English Speaking",
      north_america: "North America",
      us_only: "US Only",
      other: "Custom Territories",
    };

    html += "<dt>Selected Territory</dt>";
    html += `<dd>${
      territoryLabels[step4Data.territory] || "Not specified"
    }</dd>`;

    if (step4Data.territory === "other" && step4Data.custom_territory) {
      html += "<dt>Custom Territories</dt>";
      html += `<dd>${esc(step4Data.custom_territory)}</dd>`;
    }

    html += "</dl>";
    html += "</div>";

    // Rights availability
    html += '<div class="cell medium-6">';
    html += '<h4 class="mb-3">Rights Availability</h4>';
    html += '<dl class="info-list">';

    const rightsLabels = {
      all_exclusive: "All Rights Available Exclusively",
      some_available: "Some Rights/Platforms Available",
    };

    html += "<dt>Rights Status</dt>";
    html += `<dd>${
      rightsLabels[step4Data.rights_availability] || "Not specified"
    }</dd>`;

    if (
      step4Data.rights_availability === "some_available" &&
      step4Data.rights_explanation
    ) {
      html += "<dt>Explanation</dt>";
      html += `<dd>${esc(step4Data.rights_explanation)}</dd>`;
    }

    html += "</dl>";
    html += "</div>";

    html += "</div>"; // Close grid-x

    // Launch timing (full width below)
    if (step4Data.launch_timing) {
      html += '<div class="mt-4">';
      html += '<h4 class="mb-2">Launch Timing</h4>';
      html += '<dl class="info-list">';

      if (step4Data.launch_timing === "asap") {
        html += "<dt>When to Launch</dt>";
        html += "<dd>As Soon As Possible (after approval)</dd>";
      } else if (step4Data.launch_timing === "specific_date") {
        html += "<dt>When to Launch</dt>";
        html += "<dd>Specific Date (PST)</dd>";

        if (step4Data.launch_date) {
          html += "<dt>Scheduled Launch</dt>";
          const dateStr = step4Data.launch_date
            ? new Date(step4Data.launch_date).toLocaleDateString("en-US", {
                month: "long",
                day: "numeric",
                year: "numeric",
              })
            : "Not specified";
          html += `<dd>${esc(dateStr)} (12:00 AM PST)</dd>`;
        }
      }

      html += "</dl>";
      html += "</div>";
    }

    // Distribution notes (full width below)
    if (step4Data.distribution_notes) {
      html += '<div class="mt-4">';
      html += '<h4 class="mb-2">Additional Distribution Information</h4>';
      html += `<p class="text-muted">${esc(step4Data.distribution_notes)}</p>`;
      html += "</div>";
    }

    if (!step4Data.territory) {
      html = '<p class="text-muted">No distribution information provided</p>';
    }

    container.innerHTML = html;
  }

  /**
   * Load contact information if previously saved
   */
  function loadContactInformation() {
    // Contact data is stored in step 4 (merged with distribution data)
    const step4Data = sessionManager.getStepData(4);

    const contactNameEl = document.getElementById("contact_name");
    const contactEmailEl = document.getElementById("contact_email");
    const contactPhoneEl = document.getElementById("contact_phone");
    const companyNameEl = document.getElementById("company_name");
    const signerTitleEl = document.getElementById("signer_title");
    const addressLine1El = document.getElementById("address_line1");
    const addressLine2El = document.getElementById("address_line2");
    const cityEl = document.getElementById("city");
    const stateEl = document.getElementById("state");
    const postalCodeEl = document.getElementById("postal_code");
    const countryEl = document.getElementById("country");

    if (step4Data.contact_name && contactNameEl) {
      contactNameEl.value = step4Data.contact_name;
    }
    if (step4Data.contact_email && contactEmailEl) {
      contactEmailEl.value = step4Data.contact_email;
    }
    if (step4Data.contact_phone && contactPhoneEl) {
      contactPhoneEl.value = step4Data.contact_phone;
    }
    if (step4Data.company_name && companyNameEl) {
      companyNameEl.value = step4Data.company_name;
    }
    if (step4Data.signer_title && signerTitleEl) {
      signerTitleEl.value = step4Data.signer_title;
    }
    if (step4Data.address_line1 && addressLine1El) {
      addressLine1El.value = step4Data.address_line1;
    }
    if (step4Data.address_line2 && addressLine2El) {
      addressLine2El.value = step4Data.address_line2;
    }
    if (step4Data.city && cityEl) {
      cityEl.value = step4Data.city;
    }
    if (step4Data.state && stateEl) {
      stateEl.value = step4Data.state;
    }
    if (step4Data.postal_code && postalCodeEl) {
      postalCodeEl.value = step4Data.postal_code;
    }
    if (step4Data.country && countryEl) {
      countryEl.value = step4Data.country;
      // A resumed session may carry a free-text country from the old flow
      // (e.g. "USA") that matches no <option>; the select would silently
      // blank and block submit. Keep their value by injecting it as an option.
      if (countryEl.value !== step4Data.country) {
        const opt = document.createElement("option");
        opt.value = step4Data.country;
        opt.textContent = step4Data.country;
        countryEl.appendChild(opt);
        countryEl.value = step4Data.country;
      }
    }
    if (step4Data.terms_accepted) {
      const termsCheckbox = document.getElementById("terms_accepted");
      if (termsCheckbox) {
        termsCheckbox.checked = true;
        termsCheckbox.disabled = false;
        if (termsCheckbox.parentElement) {
          termsCheckbox.parentElement.style.opacity = "1";
        }
        const scrollHint = document.getElementById("terms-scroll-hint");
        if (scrollHint) scrollHint.style.display = "none";
      }
    }
    syncStateRequiredMark();
  }

  /**
   * Format file size for display
   */
  function formatFileSize(bytes) {
    if (!bytes || bytes === 0) return "0 Bytes";

    const k = 1024;
    const sizes = ["Bytes", "KB", "MB", "GB"];
    const i = Math.floor(Math.log(bytes) / Math.log(k));

    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + " " + sizes[i];
  }

  /**
   * Setup edit section buttons
   */
  function setupEditButtons() {
    const editButtons = document.querySelectorAll(".edit-section");

    editButtons.forEach((button) => {
      button.addEventListener("click", function () {
        const step = parseInt(this.dataset.step);

        // Save current contact form data before navigating
        saveContactFormData();

        // Navigate to the appropriate step
        let targetUrl = "";
        switch (step) {
          case 1:
            targetUrl = `${getApplyBasePath()}/imdb`;
            break;
          case 2:
            targetUrl = `${getApplyBasePath()}/upload`;
            break;
          case 3:
            targetUrl = `${getApplyBasePath()}/customize`;
            const step1Data = sessionManager.getStepData(1);
            if (step1Data.imdb_id) {
              targetUrl += `?imdb_id=${encodeURIComponent(step1Data.imdb_id)}`;
            }
            break;
          case 4:
            targetUrl = `${getApplyBasePath()}/distribution`;
            break;
        }

        if (targetUrl) {
          sessionManager.navigateWithSession(targetUrl);
        }
      });
    });
  }

  /**
   * Setup contact form auto-save
   */
  function setupContactForm() {
    const formFields = [
      "contact_name",
      "contact_email",
      "contact_phone",
      "company_name",
      "signer_title",
      "address_line1",
      "address_line2",
      "city",
      "state",
      "postal_code",
      "country",
    ];

    formFields.forEach((fieldId) => {
      const field = document.getElementById(fieldId);
      if (field) {
        field.addEventListener("blur", function () {
          saveContactFormData();
          validateSubmitButton();
        });
        // Also validate on input for required fields
        if (
          fieldId === "contact_name" ||
          fieldId === "contact_email" ||
          fieldId === "address_line1" ||
          fieldId === "city" ||
          fieldId === "state" ||
          fieldId === "postal_code" ||
          fieldId === "country"
        ) {
          field.addEventListener("input", validateSubmitButton);
        }
        // Country drives whether state is required (US only)
        if (fieldId === "country") {
          field.addEventListener("change", function () {
            syncStateRequiredMark();
            saveContactFormData();
            validateSubmitButton();
          });
        }
      }
    });

    syncStateRequiredMark();
  }

  /**
   * Save contact form data to localStorage
   */
  function saveContactFormData() {
    const contactData = {
      contact_name: document.getElementById("contact_name")?.value || "",
      contact_email: document.getElementById("contact_email")?.value || "",
      contact_phone: document.getElementById("contact_phone")?.value || "",
      company_name: document.getElementById("company_name")?.value || "",
      signer_title: document.getElementById("signer_title")?.value || "",
      address_line1: document.getElementById("address_line1")?.value || "",
      address_line2: document.getElementById("address_line2")?.value || "",
      city: document.getElementById("city")?.value || "",
      state: document.getElementById("state")?.value || "",
      postal_code: document.getElementById("postal_code")?.value || "",
      country: document.getElementById("country")?.value || "",
      terms_accepted:
        document.getElementById("terms_accepted")?.checked || false,
    };

    console.log("[Review] Saving contact data to step 4:", contactData);

    // Get existing step 4 data and merge with contact data
    const step4Data = sessionManager.getStepData(4) || {};
    const mergedData = { ...step4Data, ...contactData };

    sessionManager.saveStepData(4, mergedData);
  }

  /**
   * Setup terms checkboxes
   */
  function setupTermsCheckboxes() {
    const termsCheckbox = document.getElementById("terms_accepted");
    const termsContent = document.getElementById("terms_content");

    if (termsCheckbox) {
      termsCheckbox.addEventListener("change", function () {
        saveContactFormData();
        validateSubmitButton();
      });

      // Show notification if user tries to click disabled checkbox
      // We need to listen on the label because disabled inputs don't receive clicks
      const termsLabel = document.querySelector('label[for="terms_accepted"]');
      if (termsLabel) {
        termsLabel.style.cursor = "pointer";
        termsLabel.addEventListener("click", function (e) {
          if (termsCheckbox.disabled) {
            e.preventDefault();
            showNotification(
              "Please scroll through and review the entire agreement before agreeing to the terms.",
              "warning"
            );
          }
        });
      }

      // Also try to capture any clicks on the checkbox itself (for browsers that support it)
      termsCheckbox.addEventListener("click", function (e) {
        if (this.disabled) {
          e.preventDefault();
          showNotification(
            "Please scroll through and review the entire agreement before agreeing to the terms.",
            "warning"
          );
        }
      });
    }

    // Enable checkbox only when user scrolls to bottom of terms
    if (termsContent && termsCheckbox) {
      termsContent.addEventListener("scroll", function () {
        const scrolledToBottom =
          Math.abs(
            termsContent.scrollHeight -
              termsContent.scrollTop -
              termsContent.clientHeight
          ) < 5; // 5px threshold for rounding errors

        if (scrolledToBottom && termsCheckbox.disabled) {
          termsCheckbox.disabled = false;
          // Visual feedback that checkbox is now available
          termsCheckbox.parentElement.style.opacity = "1";
          const scrollHint = document.getElementById("terms-scroll-hint");
          if (scrollHint) scrollHint.style.display = "none";
        }
      });

      // If the agreement is short enough that it does not scroll, the scroll
      // handler would never fire and the checkbox would stay locked forever,
      // making submission impossible. Unlock it up front in that case.
      const notScrollable =
        termsContent.scrollHeight <= termsContent.clientHeight + 5;
      if (notScrollable) {
        termsCheckbox.disabled = false;
        termsCheckbox.parentElement.style.opacity = "1";
        const scrollHint = document.getElementById("terms-scroll-hint");
        if (scrollHint) scrollHint.style.display = "none";
      } else {
        // Set initial opacity to indicate checkbox is disabled
        termsCheckbox.parentElement.style.opacity = "0.5";
        termsCheckbox.parentElement.style.transition = "opacity 0.3s ease";
      }
    }
  }

  /**
   * Setup submit button
   */
  function setupSubmitButton() {
    const submitButton = document.getElementById("submit-button");
    if (!submitButton) return;

    // Prevent duplicate event listener attachment
    if (submitButton.dataset.initialized === "true") {
      console.log("[Review] Submit button already initialized, skipping");
      return;
    }
    submitButton.dataset.initialized = "true";

    submitButton.addEventListener("click", handleSubmission);

    // Initial validation
    validateSubmitButton();
  }

  /**
   * State/province stays optional outside the US: international partners
   * often have nothing sensible to put there, and it was blocking real
   * submissions (spec Phase 0). Server-side mirror lives in submitFinal.
   */
  function stateIsRequired() {
    const country = (document.getElementById("country")?.value || "")
      .trim()
      .toLowerCase();
    return country === "united states" || country === "usa" || country === "us";
  }

  function syncStateRequiredMark() {
    const mark = document.getElementById("state-required-mark");
    if (mark) mark.style.display = stateIsRequired() ? "" : "none";
  }

  /**
   * Validate and enable/disable submit button
   */
  function validateSubmitButton() {
    const submitButton = document.getElementById("submit-button");
    if (!submitButton) return;

    const contactName = document.getElementById("contact_name")?.value;
    const contactEmail = document.getElementById("contact_email")?.value;
    const addressLine1 = document.getElementById("address_line1")?.value;
    const city = document.getElementById("city")?.value;
    const state = document.getElementById("state")?.value;
    const postalCode = document.getElementById("postal_code")?.value;
    const country = document.getElementById("country")?.value;
    const termsAccepted = document.getElementById("terms_accepted")?.checked;

    // Check if CAP is solved (if enabled)
    const capValid =
      !window.capConfig?.enabled || (capSolver && capSolver.isSolved());

    // Enable if all required fields are filled and CAP is solved
    if (
      contactName &&
      contactEmail &&
      addressLine1 &&
      city &&
      (state || !stateIsRequired()) &&
      postalCode &&
      country &&
      termsAccepted &&
      capValid
    ) {
      submitButton.disabled = false;
    } else {
      submitButton.disabled = true;
    }
  }

  // Flag to prevent duplicate submissions
  let isSubmitting = false;

  /**
   * Handle form submission
   */
  async function handleSubmission() {
    // Check if already submitting
    if (isSubmitting) {
      console.log(
        "[Submission] Already submitting, preventing duplicate submission"
      );
      return;
    }

    // Disable button IMMEDIATELY to prevent double-clicks
    const submitButton = document.getElementById("submit-button");
    if (submitButton.disabled) {
      console.log(
        "[Submission] Button already disabled, preventing duplicate submission"
      );
      return;
    }

    // Set flags to prevent duplicate submissions
    isSubmitting = true;
    submitButton.disabled = true;
    submitButton.innerHTML =
      '<i class="fas fa-spinner fa-spin"></i> Submitting...';

    // Save contact form data first
    saveContactFormData();

    // Validate required fields
    if (!validateForm()) {
      // Re-enable button if validation fails
      isSubmitting = false;
      submitButton.disabled = false;
      submitButton.innerHTML =
        '<i class="fas fa-paper-plane"></i> Submit Application';
      return;
    }

    // Validate that we have all required step data
    const step1Data = sessionManager.getStepData(1);
    if (!step1Data || !step1Data.imdb_id) {
      showNotification(
        "Missing film information. Please start from the beginning and complete all steps.",
        "error"
      );
      console.error("[Submission] Missing step 1 data or IMDB ID:", step1Data);
      isSubmitting = false;
      submitButton.disabled = false;
      submitButton.innerHTML =
        '<i class="fas fa-paper-plane"></i> Submit Application';
      return;
    }

    const step4Data = sessionManager.getStepData(4);
    if (!step4Data || !step4Data.territory) {
      showNotification(
        "Missing distribution information. Please complete all previous steps.",
        "error"
      );
      console.error("[Submission] Missing step 4 data:", step4Data);
      isSubmitting = false;
      submitButton.disabled = false;
      submitButton.innerHTML =
        '<i class="fas fa-paper-plane"></i> Submit Application';
      return;
    }

    // Show loading state
    showSubmissionStatus("Submitting your application...", "info");

    try {
      // Get CAP data
      const capChallenge =
        document.getElementById("cap_challenge")?.value || "";
      const capSolution = document.getElementById("cap_solution")?.value || "";

      // Get theme from URL (bloodstream, default, etc.)
      const theme = getThemeKey();

      // Compile all data from localStorage
      const submissionData = {
        session_id: sessionManager.getSessionId(),
        step1: sessionManager.getStepData(1),
        step2: sessionManager.getStepData(2),
        step3: sessionManager.getStepData(3),
        step4: sessionManager.getStepData(4),
        theme: theme, // Track which theme/platform the submission came from
        cap_challenge: capChallenge,
        cap_solution: capSolution,
        submitted_at: new Date().toISOString(),
      };

      // Log submission data for debugging
      console.log("[Submission] Submitting data:", {
        session_id: submissionData.session_id,
        step1_has_imdb: !!submissionData.step1?.imdb_id,
        step1_keys: Object.keys(submissionData.step1 || {}),
        step2_keys: Object.keys(submissionData.step2 || {}),
        step3_keys: Object.keys(submissionData.step3 || {}),
        step4_keys: Object.keys(submissionData.step4 || {}),
      });

      // Submit to API
      const response = await fetch("/api/submissions/submit-final", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        },
        body: JSON.stringify(submissionData),
      });

      const result = await response.json();

      // Log full response for debugging
      console.log("[Submission] API Response:", result);

      if (result.success) {
        // Mark session as submitted BEFORE clearing to prevent re-submission
        sessionManager.markAsSubmitted();

        // Clear session data
        sessionManager.clearSession();

        // Redirect to analyzing page with submission ID
        const submissionId =
          result.data.submission_id || result.data.reference_number;
        window.location.href = `${getApplyBasePath()}/analyzing?submission_id=${encodeURIComponent(
          submissionId
        )}&theme=${encodeURIComponent(theme)}`;
      } else {
        const errorMessage =
          result.message ||
          result.error ||
          "Submission failed. Please try again.";
        console.error("[Submission] Error:", errorMessage);
        showSubmissionStatus(errorMessage, "error");
        isSubmitting = false;
        submitButton.disabled = false;
        submitButton.innerHTML =
          '<i class="fas fa-paper-plane"></i> Submit Application';
      }
    } catch (error) {
      console.error("Submission error:", error);
      showSubmissionStatus("An error occurred. Please try again.", "error");
      isSubmitting = false;
      submitButton.disabled = false;
      submitButton.innerHTML =
        '<i class="fas fa-paper-plane"></i> Submit Application';
    } finally {
      // Always reset the flag after attempt completes
      isSubmitting = false;
    }
  }

  /**
   * Validate form before submission
   */
  function validateForm() {
    const contactName = document.getElementById("contact_name")?.value;
    const contactEmail = document.getElementById("contact_email")?.value;
    const addressLine1 = document.getElementById("address_line1")?.value;
    const city = document.getElementById("city")?.value;
    const state = document.getElementById("state")?.value;
    const postalCode = document.getElementById("postal_code")?.value;
    const country = document.getElementById("country")?.value;
    const termsAccepted = document.getElementById("terms_accepted")?.checked;

    if (!contactName) {
      showNotification("Please enter your contact name", "warning");
      document.getElementById("contact_name").focus();
      return false;
    }

    if (!contactEmail) {
      showNotification("Please enter your email address", "warning");
      document.getElementById("contact_email").focus();
      return false;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(contactEmail)) {
      showNotification("Please enter a valid email address", "warning");
      document.getElementById("contact_email").focus();
      return false;
    }

    if (!addressLine1) {
      showNotification("Please enter your address", "warning");
      document.getElementById("address_line1").focus();
      return false;
    }

    if (!city) {
      showNotification("Please enter your city", "warning");
      document.getElementById("city").focus();
      return false;
    }

    if (!state && stateIsRequired()) {
      showNotification("Please enter your state", "warning");
      document.getElementById("state").focus();
      return false;
    }

    if (!postalCode) {
      showNotification("Please enter your postal or zip code", "warning");
      document.getElementById("postal_code").focus();
      return false;
    }

    if (!country) {
      showNotification("Please enter your country", "warning");
      document.getElementById("country").focus();
      return false;
    }

    if (!termsAccepted) {
      showNotification(
        "Please accept the Terms & Conditions to continue",
        "warning"
      );
      return false;
    }

    return true;
  }

  /**
   * Show submission status
   */
  function showSubmissionStatus(message, type) {
    const statusCard = document.getElementById("submission-status-card");
    const statusContent = document.getElementById("submission-status");

    if (statusCard && statusContent) {
      statusCard.style.display = "block";

      let iconClass = "fa-info-circle";
      let colorClass = "";

      switch (type) {
        case "success":
          iconClass = "fa-check-circle";
          colorClass = "text-success";
          break;
        case "error":
          iconClass = "fa-exclamation-circle";
          colorClass = "text-danger";
          break;
        case "warning":
          iconClass = "fa-exclamation-triangle";
          colorClass = "text-warning";
          break;
      }

      statusContent.innerHTML = `
        <div class="${colorClass}">
          <i class="fas ${iconClass}"></i> ${esc(message)}
        </div>
      `;
    }
  }

  /**
   * Show success modal
   */
  function showSuccessModal(data) {
    // Store submission ID globally
    currentSubmissionId = data.submission_id;

    // Hide the form and show real-time review UI
    const formContainer = document.querySelector(".submission-form-container");
    const reviewContainer = document.createElement("div");
    reviewContainer.className = "real-time-review-container theme-dark";

    reviewContainer.innerHTML = `
      <div class="review-header">
        <div class="review-icon pulsing">
          <i class="fas fa-video"></i>
        </div>
        <h1>Your Film is Being Reviewed in Real Time</h1>
        <p class="submission-id">Submission ID: <strong>${esc(data.reference_number)}</strong></p>
      </div>

      <div class="review-progress">
        <div class="countdown-timer">
          <div class="countdown-circle">
            <svg class="countdown-svg" viewBox="0 0 100 100">
              <circle class="countdown-bg" cx="50" cy="50" r="45"></circle>
              <circle class="countdown-progress" cx="50" cy="50" r="45"></circle>
            </svg>
            <div class="countdown-text">
              <span class="time-remaining">8:00</span>
              <span class="time-label">Estimated time</span>
            </div>
          </div>
        </div>

        <div class="progress-bar-container">
          <div class="progress-bar">
            <div class="progress-fill" style="width: 10%"></div>
          </div>
          <p class="progress-percentage">10%</p>
        </div>

        <div class="status-message">
          <i class="fas fa-circle-notch fa-spin"></i>
          <span>Initializing video analysis...</span>
        </div>
      </div>

      <div class="review-result" style="display: none;">
        <!-- Results will be shown here -->
      </div>

      <div class="review-info">
        <div class="info-card">
          <i class="fas fa-info-circle"></i>
          <p>Our AI is analyzing your film's content, quality, and suitability for BloodStream and other platforms.</p>
        </div>
      </div>
    `;

    // Replace form with review UI
    const container =
      formContainer || document.querySelector(".grid-container");
    if (container) {
      container.innerHTML = "";
      container.appendChild(reviewContainer);
    }

    // Start countdown and polling
    if (data.title_id) {
      startCountdown();
      startPolling(data.title_id);
    }
  }

  /**
   * Start countdown timer
   */
  let countdownInterval = null;
  let pollingInterval = null;

  function startCountdown() {
    const maxTime = 480; // 8 minutes in seconds
    let timeRemaining = maxTime;
    const circumference = 2 * Math.PI * 45; // radius = 45

    const updateCountdown = () => {
      const minutes = Math.floor(timeRemaining / 60);
      const seconds = timeRemaining % 60;
      const timeText = `${minutes}:${seconds.toString().padStart(2, "0")}`;

      const timeElement = document.querySelector(".time-remaining");
      if (timeElement) {
        timeElement.textContent = timeText;
      }

      // Update circular progress
      const progressCircle = document.querySelector(".countdown-progress");
      if (progressCircle) {
        const progress = (maxTime - timeRemaining) / maxTime;
        const offset = circumference * (1 - progress);
        progressCircle.style.strokeDasharray = `${circumference} ${circumference}`;
        progressCircle.style.strokeDashoffset = offset;
      }

      timeRemaining--;

      if (timeRemaining < 0) {
        clearInterval(countdownInterval);
        handleTimeout();
      }
    };

    updateCountdown();
    countdownInterval = setInterval(updateCountdown, 1000);
  }

  /**
   * Start polling for analysis status
   */
  function startPolling(titleId) {
    let pollCount = 0;
    const maxPolls = 48; // 8 minutes / 10 seconds = 48 polls

    const poll = async () => {
      try {
        const response = await fetch(
          `/api/public/titles/${titleId}/analysis-status`
        );
        const data = await response.json();

        if (data.success) {
          // Update progress
          updateProgress(data.data);

          // Check if completed
          if (data.data.is_completed) {
            clearInterval(pollingInterval);
            clearInterval(countdownInterval);
            showAnalysisResult(data.data);
            return;
          }

          // Check if failed
          if (data.data.is_failed) {
            clearInterval(pollingInterval);
            clearInterval(countdownInterval);
            showAnalysisError(data.data.error_message);
            return;
          }
        }

        pollCount++;
        if (pollCount >= maxPolls) {
          clearInterval(pollingInterval);
          clearInterval(countdownInterval);
          handleTimeout();
        }
      } catch (error) {
        console.error("Polling error:", error);
      }
    };

    // Poll immediately, then every 10 seconds
    poll();
    pollingInterval = setInterval(poll, 10000);
  }

  /**
   * Update progress display
   */
  function updateProgress(analysisData) {
    const { progress, status } = analysisData;

    // Update progress bar
    const progressFill = document.querySelector(".progress-fill");
    const progressPercentage = document.querySelector(".progress-percentage");
    if (progressFill && progressPercentage) {
      progressFill.style.width = `${progress}%`;
      progressPercentage.textContent = `${progress}%`;
    }

    // Update status message
    const statusMessage = document.querySelector(".status-message span");
    if (statusMessage) {
      const messages = {
        pending: "Initializing video analysis...",
        queued: "Video queued for processing...",
        submitted: "Analyzing video quality and content...",
        processing: "Evaluating content for platform suitability...",
      };
      statusMessage.textContent =
        messages[status] || "Processing your submission...";
    }
  }

  /**
   * Show analysis result
   */
  function showAnalysisResult(analysisData) {
    const {
      is_approved_for_bloodstream,
      rejection_reason,
      submission_id,
    } = analysisData;

    // Update global submission ID if provided
    if (submission_id) {
      currentSubmissionId = submission_id;
    }

    const progressSection = document.querySelector(".review-progress");
    const resultSection = document.querySelector(".review-result");

    if (!progressSection || !resultSection) return;

    // Hide progress, show result
    progressSection.style.display = "none";
    resultSection.style.display = "block";

    if (is_approved_for_bloodstream) {
      // Approved!
      resultSection.innerHTML = `
        <div class="result-success">
          <div class="result-icon">
            <i class="fas fa-check-circle"></i>
          </div>
          <h2>Great News!</h2>
          <p class="result-message">Your film has been approved for BloodStream.</p>
          <div class="next-steps">
            <h3>Next Steps</h3>
            <p>Additional verification is required. Expect an email from our team within <strong>24-48 hours</strong> to complete the onboarding process.</p>
          </div>
          <div class="button-group">
            <a href="/" class="button secondary">Return to Home</a>
            <a href="/submission/view?id=${encodeURIComponent(data.submission_id)}" class="button primary">View Submission</a>
          </div>
        </div>
      `;
    } else {
      // Rejected
      resultSection.innerHTML = `
        <div class="result-rejected">
          <div class="result-icon">
            <i class="fas fa-exclamation-circle"></i>
          </div>
          <h2>Review Complete</h2>
          <p class="result-message">Your submission doesn't meet BloodStream's content guidelines at this time.</p>
          <div class="rejection-reason">
            <h3>Reason</h3>
            <p>${
              rejection_reason ||
              "Our team will provide detailed feedback via email."
            }</p>
          </div>
          <div class="next-steps">
            <p>You may still be eligible for other platforms, or you can revise and resubmit. Our team will contact you with more information.</p>
          </div>
          <div class="button-group">
            <a href="/submission/view?id=${
              currentSubmissionId || ""
            }" class="button secondary">View Submission</a>
            <a href="mailto:support@studiodome.com" class="button primary">Contact Support</a>
          </div>
        </div>
      `;
    }
  }

  // Store submission_id for later use
  let currentSubmissionId = null;

  /**
   * Show analysis error
   */
  function showAnalysisError(errorMessage) {
    const progressSection = document.querySelector(".review-progress");
    const resultSection = document.querySelector(".review-result");

    if (!progressSection || !resultSection) return;

    progressSection.style.display = "none";
    resultSection.style.display = "block";

    resultSection.innerHTML = `
      <div class="result-error">
        <div class="result-icon">
          <i class="fas fa-times-circle"></i>
        </div>
        <h2>Analysis Error</h2>
        <p class="result-message">There was an issue analyzing your submission.</p>
        <p class="error-details">${
          errorMessage ||
          "Our team has been notified and will review your submission manually."
        }</p>
        <p>We'll email you with the results within 24-48 hours.</p>
        <div class="button-group">
          <a href="/submission/view?id=${
            currentSubmissionId || ""
          }" class="button secondary">View Submission</a>
          <a href="mailto:support@studiodome.com" class="button primary">Contact Support</a>
        </div>
      </div>
    `;
  }

  /**
   * Handle timeout
   */
  function handleTimeout() {
    const progressSection = document.querySelector(".review-progress");
    const resultSection = document.querySelector(".review-result");

    if (!progressSection || !resultSection) return;

    progressSection.style.display = "none";
    resultSection.style.display = "block";

    resultSection.innerHTML = `
      <div class="result-timeout">
        <div class="result-icon">
          <i class="fas fa-clock"></i>
        </div>
        <h2>Analysis Taking Longer Than Expected</h2>
        <p class="result-message">Your submission is still being processed.</p>
        <p>We'll email you the results as soon as the analysis is complete. This typically takes up to 24 hours.</p>
        <div class="button-group">
          <a href="/" class="button secondary">Return to Home</a>
          <a href="/submission/view?id=${
            currentSubmissionId || ""
          }" class="button primary">View Submission</a>
        </div>
      </div>
    `;
  }

  /**
   * Show notification
   */
  function showNotification(message, type) {
    const notification = document.createElement("div");
    notification.className = `callout ${type} has-icon`;
    notification.style.cssText =
      "position: fixed; top: 20px; right: 20px; z-index: 9999; max-width: 400px; animation: slideIn 0.3s ease;";
    notification.innerHTML = `
      <div class="callout-icon">
        <i class="fas fa-${
          type === "success"
            ? "check-circle"
            : type === "error"
            ? "exclamation-circle"
            : "info-circle"
        }"></i>
      </div>
      <div class="callout-content">
        <p>${esc(message)}</p>
      </div>
    `;

    document.body.appendChild(notification);

    setTimeout(() => {
      notification.style.animation = "slideOut 0.3s ease";
      setTimeout(() => {
        notification.remove();
      }, 300);
    }, 5000);
  }

  /**
   * Setup navigation
   */
  function setupNavigation() {
    const backButton = document.getElementById("back-button");
    if (backButton) {
      backButton.addEventListener("click", () => {
        // Save current data before navigating
        saveContactFormData();

        // Navigate to customize page
        const step1Data = sessionManager.getStepData(1);
        let targetUrl = `${getApplyBasePath()}/customize`;
        if (step1Data.imdb_id) {
          targetUrl += `?imdb_id=${encodeURIComponent(step1Data.imdb_id)}`;
        }
        sessionManager.navigateWithSession(targetUrl);
      });
    }
  }

  /**
   * Update progress bar
   */
  function updateProgress() {
    const progressBar = document.getElementById("progress-bar");
    if (progressBar) {
      progressBar.style.width = "100%";
    }
  }

  /**
   * Initialize CAP (Completely Automated Public Turing test)
   */
  function initializeCAP() {
    if (!window.capConfig || !window.capConfig.enabled) {
      return;
    }

    if (!window.CapSolver) {
      console.error("CapSolver not loaded");
      return;
    }

    // Render CAP widget HTML
    const container = document.getElementById("cap-widget-container");
    if (!container) {
      console.error("CAP widget container not found");
      return;
    }

    container.innerHTML = window.CapSolver.renderHTML({
      id: "cap-widget",
      hidden: false,
      compact: false,
      initialText: "Initializing...",
    });

    // Initialize CAP solver
    capSolver = new window.CapSolver({
      enabled: window.capConfig.enabled,
      challenge: window.capConfig.challenge,
      difficulty: window.capConfig.difficulty,
      solutionFieldId: "cap_solution",
      statusElementId: "cap-widget-text",
      progressBarId: "cap-widget-progress-bar",
      attemptsElementId: "cap-widget-attempts",
      onComplete: function (result) {
        console.log("CAP solved:", result);
        validateSubmitButton();
      },
      onError: function (error) {
        console.error("CAP error:", error);
        showNotification(
          "Bot protection verification failed. Please refresh and try again.",
          "error"
        );
      },
    });

    // Auto-solve CAP after a delay
    setTimeout(() => {
      capSolver.solve();
    }, 1000);
  }

  // Initialize on DOM ready
  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();
